/**
 * STAGING-LAUNCH-READINESS-002-R1 — canonical search alias dictionary (not hard-coded in UI).
 * Deterministic stages use this before AI semantic fallback.
 */

export type SearchAliasFamily = {
  /** Canonical Ukrainian label for the family. */
  canonical: string;
  aliases: string[];
  /** Title/category evidence needles required for a hit. */
  evidenceNeedles: string[];
  /**
   * Needles that are unambiguous on their own (no homonym risk) — a hit here is
   * sufficient evidence without any extra context. Used to disambiguate families
   * whose plain `evidenceNeedles` are common words shared with unrelated products
   * (e.g. "палочки" also means generic food "sticks").
   */
  strongEvidenceNeedles?: string[];
  /**
   * When set, a plain `evidenceNeedles` hit only counts as evidence if the same
   * product ALSO contains one of these context needles. Prevents homonym false
   * positives (e.g. rice-cake "sticks" snacks matching a chopsticks query).
   */
  disambiguationContext?: string[];
};

/** Product-family aliases (UA/RU/EN + common transliterations). */
export const SEARCH_PRODUCT_ALIASES: SearchAliasFamily[] = [
  {
    canonical: "палички",
    aliases: [
      "палички",
      "паличкі",
      "палочки",
      "хасі",
      "хаші",
      "chopsticks",
      "китайські палички",
      "японські палички",
      "hashi",
      "hashі",
    ],
    // "палич"/"палоч" alone are homonym-prone — many unrelated snacks are named
    // "рисові/бісквітні/шоколадні палички" (rice/biscuit/chocolate "sticks").
    // Only count them as chopsticks evidence together with real utensil context.
    evidenceNeedles: ["палич", "палоч"],
    strongEvidenceNeedles: ["chopstick", "хасі", "хаші", "hashi"],
    disambiguationContext: [
      "для їжі",
      "для еды",
      "для суши",
      "для сушi",
      "посуд",
      "кухні",
      "кухни",
      "прилад",
    ],
  },
  {
    canonical: "посуд",
    aliases: ["посуд", "посуда", "tableware", "kitchenware", "кухонне приладдя"],
    evidenceNeedles: ["посуд", "tableware", "kitchenware"],
  },
  {
    canonical: "рамен",
    aliases: ["рамен", "рамьон", "ramen", "ramyun", "рамен", "рамьон"],
    evidenceNeedles: ["рамен", "рамьон", "ramen", "ramyun", "локшин"],
  },
  {
    canonical: "локшина",
    aliases: [
      "локшина",
      "лапша",
      "noodles",
      "рисова локшина",
      "рисовая лапша",
      "рисовая локшина",
      "фунчоза",
      "удон",
      "соба",
    ],
    evidenceNeedles: ["локшин", "лапш", "noodle", "фунчоз", "удон", "соба", "udon", "soba", "рисов"],
  },
  {
    canonical: "морепродукти",
    aliases: [
      "морепродукти",
      "морепродукт",
      "морепродукты",
      "seafood",
      "морепродуктів",
    ],
    evidenceNeedles: ["морепродукт", "seafood", "кревет", "кальмар", "міді", "мидии", "ікр", "икр"],
  },
  {
    canonical: "креветки",
    aliases: ["креветки", "креветка", "креветк", "shrimp", "prawn"],
    evidenceNeedles: ["кревет", "shrimp", "prawn"],
  },
  {
    canonical: "соєвий соус",
    aliases: ["соєвий соус", "соевый соус", "soy sauce", "соєвий", "соевый"],
    evidenceNeedles: ["соєв", "соев", "soy"],
  },
  {
    canonical: "рисовий папір",
    aliases: ["рисовий папір", "рисовая бумага", "rice paper"],
    evidenceNeedles: ["рисовий пап", "рисов", "rice paper"],
  },
  {
    canonical: "місо",
    aliases: ["місо", "мисо", "miso"],
    evidenceNeedles: ["місо", "мисо", "miso"],
  },
  {
    canonical: "кімчі",
    aliases: ["кімчі", "кимчи", "kimchi"],
    evidenceNeedles: ["кімч", "кимч", "kimchi"],
  },
  {
    canonical: "шрірача",
    aliases: ["шрірача", "ширача", "срирача", "sriracha", "Ihshfxf", "ікшкфсрф"],
    evidenceNeedles: ["шрірач", "ширач", "срирач", "sriracha", "ihshfxf"],
  },
  {
    canonical: "норі",
    aliases: ["норі", "нори", "nori"],
    evidenceNeedles: ["норі", "нори", "nori"],
  },
  {
    canonical: "васабі",
    aliases: ["васабі", "васаби", "wasabi"],
    evidenceNeedles: ["васаб", "wasabi"],
  },
  {
    canonical: "кокосове молоко",
    aliases: ["кокосове молоко", "кокосовое молоко", "coconut milk"],
    evidenceNeedles: ["кокос", "coconut"],
  },
  {
    canonical: "тапіока",
    aliases: ["тапіока", "tapioca", "бабл ті", "bubble tea"],
    evidenceNeedles: ["тапіок", "tapioca", "бабл", "bubble"],
  },
  {
    canonical: "моті",
    aliases: ["моті", "мочі", "mochi"],
    evidenceNeedles: ["моті", "мочі", "mochi"],
  },
  {
    canonical: "бульдак",
    aliases: ["бульдак", "buldak", "buldak"],
    evidenceNeedles: ["бульдак", "buldak", "buldak"],
  },
  {
    canonical: "том ям",
    aliases: ["том ям", "том-ям", "tom yum", "tomyum"],
    evidenceNeedles: ["том ям", "том-ям", "tom yum", "tomyum"],
  },
  {
    canonical: "карі",
    aliases: ["карі", "карри", "curry", "паста карі"],
    evidenceNeedles: ["карі", "карри", "curry"],
  },
  {
    canonical: "спринг-роли",
    aliases: ["спринг-роли", "spring roll", "spring rolls", "роли"],
    evidenceNeedles: ["спринг", "spring roll", "рол"],
  },
  {
    canonical: "снеки",
    aliases: ["снеки", "чипси", "snacks", "chips", "snack"],
    evidenceNeedles: ["снек", "чип", "snack", "chip"],
  },
  {
    canonical: "напій",
    aliases: ["напій", "напиток", "drink", "сік", "сок", "juice"],
    evidenceNeedles: ["напій", "напит", "drink", "сік", "сок", "juice"],
  },
  {
    canonical: "рис",
    aliases: ["рис", "rice"],
    evidenceNeedles: ["рис", "rice"],
  },
  {
    canonical: "гострий соус",
    aliases: ["гострий соус", "острый соус", "hot sauce", "chili sauce"],
    evidenceNeedles: ["гостр", "остр", "hot sauce", "chili", "чилі"],
  },
  {
    canonical: "рибний соус",
    aliases: ["рибний соус", "рыбный соус", "fish sauce"],
    evidenceNeedles: ["рибн", "рыбн", "fish sauce"],
  },
  {
    canonical: "устричний соус",
    aliases: ["устричний соус", "устричный соус", "oyster sauce"],
    evidenceNeedles: ["устрич", "oyster"],
  },
  {
    canonical: "чай",
    aliases: ["чай", "tea", "пуер", "пуэр", "матча", "маття", "matcha"],
    evidenceNeedles: ["чай", "tea", "пуер", "пуэр", "матч", "матть", "matcha"],
  },
];

export function expandSearchQueryAliases(query: string): string[] {
  const q = String(query ?? "").trim().toLocaleLowerCase("uk");
  if (!q) return [];
  const out = new Set<string>([q]);
  for (const family of SEARCH_PRODUCT_ALIASES) {
    if (family.aliases.some((a) => a.toLocaleLowerCase("uk") === q || q.includes(a.toLocaleLowerCase("uk")))) {
      for (const a of family.aliases) out.add(a.toLocaleLowerCase("uk"));
      out.add(family.canonical.toLocaleLowerCase("uk"));
    }
  }
  return [...out];
}

/** True when query looks Russian-script / RU food spelling (deterministic, no LLM). */
export function isLikelyRussianFoodQuery(query: string): boolean {
  const q = String(query ?? "").trim().toLocaleLowerCase("ru");
  if (!q) return false;
  if (/[ыёэъ]/.test(q)) return true;
  const ruMarkers = [
    "лапша",
    "морепродукт",
    "соевый",
    "рисовая",
    "напиток",
    "палочки",
    "острый",
    "рыбный",
  ];
  return ruMarkers.some((m) => q.includes(m));
}

export const RU_SEARCH_SUCCESS_HINT_UK =
  "Знайшли 🙂 Але українською пошук зазвичай точніший 🇺🇦 Слава Україні!";


export function evidenceNeedlesForQuery(query: string): string[] {
  const q = String(query ?? "").trim().toLocaleLowerCase("uk");
  const needles = new Set<string>();
  for (const family of SEARCH_PRODUCT_ALIASES) {
    if (family.aliases.some((a) => a.toLocaleLowerCase("uk") === q || q.includes(a.toLocaleLowerCase("uk")))) {
      for (const n of family.evidenceNeedles) needles.add(n);
      for (const n of family.strongEvidenceNeedles ?? []) needles.add(n);
    }
  }
  return [...needles];
}

/**
 * Resolve the single alias family a query maps to (canonical query contract for
 * both the retrieval scorer and the "did you mean" suggestion). Prefers the
 * family whose matched alias is the longest/most specific, so a broad umbrella
 * family (e.g. "посуд") never shadows a more specific one (e.g. "палички") for
 * the same query.
 */
export function resolveAliasFamilyForQuery(query: string): SearchAliasFamily | null {
  const q = String(query ?? "").trim().toLocaleLowerCase("uk");
  if (!q) return null;
  let best: SearchAliasFamily | null = null;
  let bestAliasLen = -1;
  for (const family of SEARCH_PRODUCT_ALIASES) {
    for (const a of family.aliases) {
      const af = a.toLocaleLowerCase("uk");
      if (af === q || q.includes(af)) {
        if (af.length > bestAliasLen) {
          bestAliasLen = af.length;
          best = family;
        }
        break;
      }
    }
  }
  return best;
}

/**
 * R2-R6 — merge candidate omnisearch enrichment (public/data JSON) into the
 * existing alias dictionary. Curated families stay first for precedence.
 * Never invents SKU/EAN; Google image-search URLs are excluded at import time.
 */
type OmnisearchAliasFile = {
  families?: Array<{
    canonical?: string;
    aliases?: string[];
    evidenceNeedles?: string[];
    strongEvidenceNeedles?: string[] | null;
    disambiguationContext?: string[] | null;
  }>;
};

let omnisearchMerged = false;

export async function ensureOmnisearchAliasesLoaded(): Promise<void> {
  if (omnisearchMerged || typeof fetch !== "function") return;
  omnisearchMerged = true;
  try {
    const res = await fetch("/data/omnisearch-aliases-v1.json", { cache: "force-cache" });
    if (!res.ok) return;
    const data = (await res.json()) as OmnisearchAliasFile;
    const existingCanon = new Set(
      SEARCH_PRODUCT_ALIASES.map((f) => f.canonical.toLocaleLowerCase("uk")),
    );
    for (const fam of data.families ?? []) {
      const canonical = String(fam.canonical ?? "").trim();
      if (!canonical) continue;
      const key = canonical.toLocaleLowerCase("uk");
      if (existingCanon.has(key)) {
        // Extend curated family aliases only (do not replace evidence gates).
        const target = SEARCH_PRODUCT_ALIASES.find((f) => f.canonical.toLocaleLowerCase("uk") === key);
        if (!target) continue;
        for (const a of fam.aliases ?? []) {
          const al = String(a).trim();
          if (!al) continue;
          if (!target.aliases.some((x) => x.toLocaleLowerCase("uk") === al.toLocaleLowerCase("uk"))) {
            target.aliases.push(al);
          }
        }
        continue;
      }
      existingCanon.add(key);
      const next: SearchAliasFamily = {
        canonical,
        aliases: [...new Set((fam.aliases ?? []).map((a) => String(a).trim()).filter(Boolean))].slice(0, 24),
        evidenceNeedles: [...new Set((fam.evidenceNeedles ?? []).map((a) => String(a).trim()).filter(Boolean))].slice(
          0,
          12,
        ),
      };
      if (fam.strongEvidenceNeedles?.length) next.strongEvidenceNeedles = fam.strongEvidenceNeedles.filter(Boolean);
      if (fam.disambiguationContext?.length) next.disambiguationContext = fam.disambiguationContext.filter(Boolean);
      if (next.aliases.length && next.evidenceNeedles.length) SEARCH_PRODUCT_ALIASES.push(next);
    }
  } catch {
    /* soft-fail — curated dictionary remains */
  }
}
